/**
 * PaymentScheduleModal - Import payment schedules from PDF/Excel files
 */

import { useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useCashflowStore } from '../../stores/cashflowStore';
import {
  X,
  Upload,
  FileSpreadsheet,
  FileText,
  Image,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Square,
  CheckSquare,
  Calendar,
  Banknote,
  Info,
  Building2,
  CreditCard,
} from 'lucide-react';

// Account type options
const ACCOUNT_TYPES = [
  { value: '', label: 'Seçiniz' },
  { value: 'bank', label: 'Banka Hesabı' },
  { value: 'credit_card', label: 'Kredi Kartı' },
  { value: 'cash', label: 'Nakit' },
  { value: 'other', label: 'Diğer' },
];

const PaymentScheduleModal = () => {
  const {
    isScheduleModalOpen,
    closeScheduleModal,
    isParsingSchedule,
    parsedSchedule,
    scheduleParseError,
    selectedPayments,
    parseScheduleFile,
    togglePaymentSelection,
    selectAllPayments,
    selectFuturePayments,
    deselectAllPayments,
    importSelectedPayments,
  } = useCashflowStore();

  const fileInputRef = useRef(null);

  // Import settings state
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState('');
  const [markPastAsPaid, setMarkPastAsPaid] = useState(true);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await parseScheduleFile(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [parseScheduleFile]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await parseScheduleFile(file);
    }
  }, [parseScheduleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const today = new Date().toISOString().split('T')[0];

  if (!isScheduleModalOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Ödeme Takvimi İçe Aktar</h2>
                <p className="text-sm text-gray-400">PDF, Excel veya görsel dosyasından ödemeleri çıkar</p>
              </div>
            </div>
            <button
              onClick={closeScheduleModal}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Upload area - show when no schedule parsed */}
            {!parsedSchedule && !isParsingSchedule && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-indigo-500/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-300 mb-2">
                  Dosyayı sürükleyip bırakın veya tıklayın
                </p>
                <p className="text-sm text-gray-500">
                  PDF, Excel, CSV ve görsel dosyaları (PNG, JPG) desteklenir
                </p>
                <div className="flex items-center justify-center gap-4 mt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileText className="w-4 h-4" />
                    <span>PDF</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Excel</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Image className="w-4 h-4" />
                    <span>Görsel</span>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state */}
            {isParsingSchedule && !parsedSchedule && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-indigo-400 animate-spin mb-4" />
                <p className="text-gray-300">Dosya analiz ediliyor...</p>
                <p className="text-sm text-gray-500 mt-1">Bu işlem birkaç saniye sürebilir</p>
              </div>
            )}

            {/* Error state */}
            {scheduleParseError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Hata</p>
                    <p className="text-red-300 text-sm mt-1">{scheduleParseError}</p>
                  </div>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                >
                  Tekrar Dene
                </button>
              </div>
            )}

            {/* Parsed schedule */}
            {parsedSchedule && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-white">{parsedSchedule.creditor || 'Ödeme Takvimi'}</h3>
                      <p className="text-sm text-gray-400 mt-1">{parsedSchedule.summary}</p>
                    </div>
                    <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 text-xs rounded-full">
                      {parsedSchedule.documentType}
                    </span>
                  </div>

                  {parsedSchedule.metadata?.loan_type && (
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-3 pt-3 border-t border-gray-700">
                      <span>Kredi Türü: {parsedSchedule.metadata.loan_type}</span>
                      {parsedSchedule.metadata.interest_rate && (
                        <span>Faiz: {parsedSchedule.metadata.interest_rate}</span>
                      )}
                      {parsedSchedule.totalAmount && (
                        <span>Toplam: {formatCurrency(parsedSchedule.totalAmount, parsedSchedule.currency)}</span>
                      )}
                    </div>
                  )}

                  {parsedSchedule.warnings?.length > 0 && (
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-700">
                      <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-300">{parsedSchedule.warnings.join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Import settings */}
                <div className="bg-gray-800/50 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-medium text-gray-300">İçe Aktarım Ayarları</h4>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Account name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <Building2 className="w-4 h-4" />
                        Hesap Adı
                      </label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="örn: Enpara, Akbank"
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none text-sm"
                      />
                    </div>

                    {/* Account type */}
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <CreditCard className="w-4 h-4" />
                        Hesap Tipi
                      </label>
                      <select
                        value={accountType}
                        onChange={(e) => setAccountType(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-indigo-500 focus:outline-none text-sm"
                      >
                        {ACCOUNT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Mark past as paid checkbox */}
                  <div
                    onClick={() => setMarkPastAsPaid(!markPastAsPaid)}
                    className="flex items-center gap-3 p-3 bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition-colors"
                  >
                    {markPastAsPaid ? (
                      <CheckSquare className="w-5 h-5 text-indigo-400" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <p className="text-sm text-white">Geçmiş ödemeleri "Ödendi" olarak işaretle</p>
                      <p className="text-xs text-gray-400">Bugünden önceki tarihlere sahip ödemeler tamamlanmış olarak kaydedilir</p>
                    </div>
                  </div>
                </div>

                {/* Selection controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllPayments}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Tümünü Seç
                    </button>
                    <button
                      onClick={selectFuturePayments}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Gelecek Ödemeler
                    </button>
                    <button
                      onClick={deselectAllPayments}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Temizle
                    </button>
                  </div>
                  <span className="text-sm text-gray-400">
                    {selectedPayments.length} / {parsedSchedule.payments.length} seçili
                  </span>
                </div>

                {/* Payments list */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {parsedSchedule.payments.map((payment) => {
                    const isSelected = selectedPayments.includes(payment.tempId);
                    const isPast = payment.due_date < today;

                    return (
                      <div
                        key={payment.tempId}
                        onClick={() => togglePaymentSelection(payment.tempId)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-500/20 border border-indigo-500/30'
                            : 'bg-gray-800/30 border border-gray-700/50 hover:bg-gray-800/50'
                        } ${isPast ? 'opacity-60' : ''}`}
                      >
                        {/* Checkbox */}
                        <div className="flex-shrink-0">
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-indigo-400" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-500" />
                          )}
                        </div>

                        {/* Payment info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white truncate">
                              {payment.title}
                            </span>
                            {payment.installment_number && payment.total_installments && (
                              <span className="text-xs text-gray-400">
                                ({payment.installment_number}/{payment.total_installments})
                              </span>
                            )}
                            {isPast && (
                              <span className="px-1.5 py-0.5 bg-gray-600 text-gray-300 text-xs rounded">
                                Geçmiş
                              </span>
                            )}
                          </div>
                          {payment.description && (
                            <p className="text-sm text-gray-400 truncate">{payment.description}</p>
                          )}
                        </div>

                        {/* Date */}
                        <div className="flex items-center gap-1.5 text-sm text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {format(new Date(payment.due_date), 'd MMM yyyy', { locale: tr })}
                          </span>
                        </div>

                        {/* Amount */}
                        <div className="flex items-center gap-1.5 text-red-400 font-medium">
                          <Banknote className="w-4 h-4" />
                          <span>{formatCurrency(payment.amount, payment.currency)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Upload another file */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Farklı dosya yükle
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          {parsedSchedule && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 bg-gray-900/50">
              <div className="text-sm text-gray-400">
                {selectedPayments.length > 0 && (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {selectedPayments.length} ödeme içe aktarılacak
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeScheduleModal}
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => importSelectedPayments({ accountName, accountType, markPastAsPaid })}
                  disabled={selectedPayments.length === 0 || isParsingSchedule}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-400 text-white rounded-lg transition-colors"
                >
                  {isParsingSchedule ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>İçe Aktarılıyor...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>İçe Aktar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentScheduleModal;
